import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { formatFileSize } from '~/eds/src/utils/formatFileSize';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { TicketDocument } from '~/types/Ticket';
import { openInNewTab } from '~/utils/browser';
import { getFileIconNameByFileType } from '~/utils/fileTypeIcon';

const messages = defineMessages({
  version: {
    id: 'DocumentItem.version',
    defaultMessage: 'Version {version}',
    description: 'Version chip label',
  },
  uploadedBy: {
    id: 'DocumentItem.uploadedBy',
    defaultMessage: 'Uploaded by {name} {date} • {size}',
    description: 'Uploader info text with date and file size',
  },
  viewTicket: {
    id: 'DocumentItem.viewTicket',
    defaultMessage: 'View Ticket',
    description: 'Action menu item to view the ticket in a new tab',
  },
  removeTicket: {
    id: 'DocumentItem.removeTicket',
    defaultMessage: 'Remove Ticket',
    description: 'Action menu item to remove the ticket from the bundle',
  },
  removeTicketDisabled: {
    id: 'DocumentItem.removeTicketDisabled',
    defaultMessage: 'A bundle must have at least 2 documents',
    description: 'Tooltip explaining why remove is disabled',
  },
});

interface DocumentItemProps {
  document: TicketDocument;
  ticketId: string;
  isDragging?: boolean;
  validationError?: string;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  onRemove?: (ticketId: string) => void;
  canRemove?: boolean;
}

export const DocumentItem = ({
  document,
  ticketId,
  isDragging,
  validationError,
  dragHandleProps,
  onRemove,
  canRemove = true,
}: DocumentItemProps) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const client = useClientSubDomain();
  const fileIcon = getFileIconNameByFileType(document.fileType);

  const actions = useMemo(
    () => [
      {
        label: intl.formatMessage(messages.viewTicket),
        value: 'view-ticket' as const,
        onClick: () => {
          openInNewTab(`/${client}/workflow/tickets/${ticketId}`);
        },
      },
      ...(onRemove
        ? [
            {
              label: intl.formatMessage(messages.removeTicket),
              value: 'remove-ticket' as const,
              disabled: !canRemove,
              ...(!canRemove && {
                disabledReason: intl.formatMessage(
                  messages.removeTicketDisabled,
                ),
              }),
              onClick: () => onRemove(ticketId),
            },
          ]
        : []),
    ],
    [intl, client, ticketId, onRemove, canRemove],
  );

  return (
    <Layout direction="column" spacing={1}>
      <Layout align="flex-start" justify="space-between">
        <Layout align="flex-start" spacing={2} flex="auto">
          {dragHandleProps && (
            <Layout
              align="center"
              styles={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              {...dragHandleProps}
            >
              <Icon icon="reorder" />
            </Layout>
          )}
          <Icon icon={fileIcon} />
          <Layout direction="column" spacing={0.5}>
            <Text variant="body-medium">{document.name}</Text>
            <Text variant="tiny" color="text.secondary">
              {intl.formatMessage(messages.uploadedBy, {
                name: document.lastModifiedByEmail,
                date: formatDate(document.modifiedDate, 'short'),
                size: formatFileSize(document.fileSize, intl),
              })}
            </Text>
            {validationError && (
              <Text variant="tiny" color="status.danger">
                {validationError}
              </Text>
            )}
          </Layout>
        </Layout>
        <Layout align="center" spacing={1}>
          <Chip
            preset="info"
            text={intl.formatMessage(messages.version, {
              version: document.version,
            })}
          />
          <ActionsMenu
            enablePortal
            actions={actions}
            name={`document-actions-${ticketId}`}
            icon="more"
            size="s"
          />
        </Layout>
      </Layout>
    </Layout>
  );
};
